import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icons } from '@/assets';

interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
}

type ConfirmCallback = (confirmed: boolean) => void;

const ConfirmDialogContext = React.createContext<{
  show: (options: ConfirmDialogOptions) => Promise<boolean>;
} | null>(null);

export const useConfirmDialog = () => {
  const context = React.useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions>({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDangerous: false,
  });
  const [callback, setCallback] = useState<ConfirmCallback | null>(null);

  const show = useCallback(
    (dialogOptions: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setOptions({
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          isDangerous: false,
          ...dialogOptions,
        });
        setCallback(() => (confirmed: boolean) => {
          resolve(confirmed);
          setIsOpen(false);
        });
        setIsOpen(true);
      });
    },
    []
  );

  const handleConfirm = () => {
    if (callback) callback(true);
  };

  const handleCancel = () => {
    if (callback) callback(false);
  };

  return (
    <ConfirmDialogContext.Provider value={{ show }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleCancel}
              className="fixed inset-0 bg-black/50 z-40"
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  handleCancel();
                }
              }}
            >
              <div
                className="bg-white rounded-lg shadow-lg max-w-sm w-full overflow-hidden"
                style={{
                  border: '1px solid #E5E7EB',
                }}
              >
                {/* Header */}
                <div className="px-4 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {options.title}
                  </h2>
                </div>

                {/* Content */}
                <div className="px-4 py-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {options.message}
                  </p>
                </div>

                {/* Footer */}
                <div className="px-2 py-2 bg-gray-50 flex gap-3 justify-end border-t border-gray-200">
                  <button
                    onClick={handleCancel}
                    className="px-2 py-2 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    {options.cancelText}
                  </button>
                  <button
                    onClick={handleConfirm}
                    className={`px-2 py-2 rounded-md text-sm font-medium text-white transition-colors ${options.isDangerous
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                  >
                    {options.confirmText}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ConfirmDialogContext.Provider>
  );
};

export default ConfirmDialogProvider;
